import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Typography } from "@mui/material";
import { AccessTimeOutlined } from "@mui/icons-material";
import { translate } from "../../../../translate/translate";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import { ErrorComponent } from "../../../../components/error/error.component";
import {
  Analysis,
  AnalysisResultExtractType,
  ForecastExtractData,
  ForecastExtractItem,
} from "../../../../models/general/analysis";
import {
  Calc,
  CardHeader,
  CellLabel,
  Chip,
  DialogBar,
  ExtractCard,
  Foot,
  ParentRef,
  Qty,
  Row,
  Rows,
  SummaryRow,
  T,
  calcText,
  formatNegative,
  formatValue,
} from "./extract-shared";

/** A cell BOM: its demand, what the finished goods offset, and the semi-finished items consumed. */
interface FinalProductBlock {
  code: string;
  priority: number;
  forecast?: ForecastExtractItem;
  finishedGood?: ForecastExtractItem;
  semiFinished: ForecastExtractItem[];
}

const DialogResultForecast = (props: any) => {
  const { open, onClose, title, analysisResultIds } = props;

  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const [data, setData] = React.useState<ForecastExtractData>();
  const [isProcessingError, setIsProcessingError] = React.useState<boolean>(false);

  const handleCloseDialog = () => {
    onClose();
  };

  useEffect(() => {
    getForecastInfo();
  }, [analysisResultIds]);

  const getForecastInfo = () => {
    if (analysisResultIds.length === 0) return;
    setIsProcessingError(false);
    service.clearParameters();
    service.addParameter("analysis_result_ids", analysisResultIds);
    service.addParameter("extract_type", AnalysisResultExtractType.FORECAST);
    service
      .getAllFromListRoute("extract")
      .then((response: any) => {
        setData(response?.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 409 || error?.response?.status === 404) {
          setIsProcessingError(true);
        } else {
          ErrorComponent(error);
        }
      });
  };

  const demandOf = (item?: ForecastExtractItem): number =>
    (item?.quantity || 0) * (item?.bom_component?.usage_quantity || 1);

  /**
   * Groups the flat item list into one block per BOM, ordered by calculation order. A
   * semi-finished item is nested under the final product that consumed it
   * (`parent_final_product`) instead of appearing as a sibling code.
   */
  const finalProductBlocks = (): FinalProductBlock[] => {
    const blocks = new Map<string, FinalProductBlock>();

    const blockOf = (code: string, priority?: number): FinalProductBlock => {
      const existing = blocks.get(code);
      if (existing) return existing;
      const created: FinalProductBlock = { code, priority: priority || 1, semiFinished: [] };
      blocks.set(code, created);
      return created;
    };

    data?.forecast?.group?.items?.forEach((item) => {
      blockOf(item.code, item.priority).forecast = item;
    });

    data?.finished_goods?.group?.items?.forEach((item) => {
      if (item.parent_final_product) {
        blockOf(item.parent_final_product, item.priority).semiFinished.push(item);
        return;
      }
      blockOf(item.code, item.priority).finishedGood = item;
    });

    return Array.from(blocks.values()).sort((a, b) => a.priority - b.priority);
  };

  const blocks = data ? finalProductBlocks() : [];
  const customerName = data?.forecast?.name;
  const orderLabel = (priority: number) => translate.t("calculation_order").replace("{n}", String(priority));

  const renderBlock = (block: FinalProductBlock, last: boolean) => {
    const forecastDemand = demandOf(block.forecast);
    const finishedGoodDemand = demandOf(block.finishedGood);
    const semiDemand = block.semiFinished.reduce((sum, item) => sum + demandOf(item), 0);
    const netDemand = forecastDemand - finishedGoodDemand - semiDemand;

    const forecastCalc = calcText(block.forecast?.quantity || 0, block.forecast?.bom_component?.usage_quantity);
    const finishedGoodCalc = calcText(
      block.finishedGood?.quantity || 0,
      block.finishedGood?.bom_component?.usage_quantity
    );

    // The badge reflects the component role in this BOM (primary/alternative + consumption
    // order), independent of the calculation order shown in the subtitle.
    const badgeItem = block.forecast ?? block.finishedGood;
    const isAlternative = badgeItem?.purchase_priority === 2;
    const consumptionOrder = badgeItem?.consumption_priority ?? 1;
    const badgeLabel = `${isAlternative ? "ALT" : "PR"}-${consumptionOrder}`;

    return (
      <ExtractCard key={block.code} last={last}>
        <CardHeader
          railColor={T.used}
          name={block.code}
          kind={customerName ? `${customerName} · ${orderLabel(block.priority)}` : orderLabel(block.priority)}
          right={<Chip variant={isAlternative ? "alt" : "pr"}>{badgeLabel}</Chip>}
        />

        <Rows>
          {block.forecast && (
            <Row
              tint="used"
              left={
                <>
                  <CellLabel>{translate.t("customer_forecast")}</CellLabel>
                  {forecastCalc && <Calc>{forecastCalc}</Calc>}
                </>
              }
              qty={<Qty tone="used">{formatValue(forecastDemand)}</Qty>}
            />
          )}

          {block.finishedGood && block.finishedGood.quantity !== 0 && (
            <Row
              left={
                <>
                  <CellLabel>{translate.t("finished_goods")}</CellLabel>
                  {finishedGoodCalc && <Calc>{finishedGoodCalc}</Calc>}
                </>
              }
              qty={<Qty tone="neg">{formatNegative(finishedGoodDemand)}</Qty>}
            />
          )}

          {block.semiFinished.map((item) => {
            const semiCalc = calcText(item.quantity, item.bom_component?.usage_quantity);
            return (
              <Row
                key={item.code}
                left={
                  <>
                    <ParentRef code={item.code} label={translate.t("semi_finished_short")} />
                    {semiCalc && <Calc>{semiCalc}</Calc>}
                  </>
                }
                qty={<Qty tone="neg">{formatNegative(demandOf(item))}</Qty>}
              />
            );
          })}
        </Rows>

        {/* It is this number — not the gross one — that the other three extracts consume. */}
        <SummaryRow label={translate.t("net_demand")} value={formatValue(netDemand)} tone="used" />
      </ExtractCard>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogBar label={translate.t("forecast")} pn={title} onClose={handleCloseDialog} />

      <DialogContent sx={{ maxHeight: "450px", overflowY: "auto", padding: 0, background: T.surface }}>
        {isProcessingError ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              minHeight: "300px",
              width: 380,
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 64, color: "#CFD8DC" }} />
            <Typography sx={{ color: T.inkSoft, fontWeight: 600, textAlign: "center", fontSize: "16px", maxWidth: "340px" }}>
              {translate.t("cell_data_processing")}
            </Typography>
          </Box>
        ) : (
          <Box>{blocks.map((block, index) => renderBlock(block, index === blocks.length - 1))}</Box>
        )}
      </DialogContent>

      {!isProcessingError && (
        <Foot label={translate.t("total_demand")} value={`${formatValue(data?.total)} ${translate.t("unit")}`} />
      )}
    </Dialog>
  );
};

export default DialogResultForecast;
